import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Reports } from './Reports';
import { BIC } from './BIC';
import UserManagement from './Admin/UserManagementRedesigned';
import GroupStoresManager from './Admin/GroupStoresManager';
import PermissionsManager from './Admin/PermissionsManager';
import { AuthProvider, useAuth } from '../components/Auth/AuthProvider';
import { LoginForm } from '../components/Auth/LoginForm';
import { TenantProvider } from '../components/TenantContext';
import { TenantSelectorCompact } from '../components/TenantSelector';
import { CompactAppBar } from '../components/CompactAppBar';
import { Box, Typography } from '@mui/material';
import { PasswordUpgradePrompt } from '../components/Auth/PasswordUpgradePrompt';
import { usePasswordUpgradePrompt } from '../hooks/usePasswordUpgradePrompt';

const LegacyAppContent: React.FC = () => {
  const { user, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const { showPrompt, dismissPrompt } = usePasswordUpgradePrompt();

  const handleSignOut = async () => {
    await signOut();
    window.location.href = '/login';
  };

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!user) {
    return <LoginForm />;
  }

  const path = typeof window !== 'undefined' ? window.location.pathname : '/';
  const isBic = path === '/bic';
  const isAdminUsers = path === '/admin/users';
  const isAdminGroupsStores = path === '/admin/groups-stores';
  const isAdminPermissions = path === '/admin/permissions';
  const pageTitle = isBic
    ? 'BIC Classifications'
    : isAdminUsers
      ? 'Admin — User Management'
      : isAdminGroupsStores
        ? 'Admin — Groups & Stores'
        : isAdminPermissions
            ? 'Admin — Permissions'
            : 'OLSG Daily Report';

  // Simplified navigation - matching OpenTrack/DealerVault pages
  const navigationLinks = useMemo(
    () => [
      { label: 'Covered RO Parts', path: '/reports/covered-parts', active: false },
      // { label: 'Dealervault Report', path: '/reports/dealervault', active: false },
      {
        label: 'OT/DV Classic View',
        path: '/reports',
        active:
          path === '/reports' &&
          !isBic &&
          !isAdminUsers &&
          !isAdminGroupsStores &&
          !isAdminPermissions,
      },
      { label: 'Covered Chemicals', path: '/reports/chemicals', active: false },
      {
        label: 'Admin Settings',
        path: '/admin',
        active: isAdminUsers || isAdminGroupsStores || isAdminPermissions,
      },
    ],
    [path, isBic, isAdminUsers, isAdminGroupsStores, isAdminPermissions],
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <CompactAppBar
        title={pageTitle}
        userEmail={user?.email || ''}
        onSignOut={handleSignOut}
        onNavigate={handleNavigate}
        navigationLinks={navigationLinks}
        tenantSelector={<TenantSelectorCompact />}
      />
      <Box sx={{ px: 3, py: 2, maxWidth: '98vw', mx: 'auto' }}>
        {showPrompt && <PasswordUpgradePrompt onDismiss={dismissPrompt} />}
        {isBic ? (
          <BIC />
        ) : isAdminUsers ? (
          <UserManagement />
        ) : isAdminGroupsStores ? (
          <GroupStoresManager />
        ) : isAdminPermissions ? (
          <PermissionsManager />
        ) : (
          <Reports />
        )}
      </Box>
    </Box>
  );
};

export const LegacyApp: React.FC = () => {
  return (
    <AuthProvider>
      <TenantProviderWithAuth />
    </AuthProvider>
  );
};

export { LegacyAppContent };

function TenantProviderWithAuth() {
  const { isSuperAdmin } = useAuth();
  return (
    <TenantProvider isSuperAdmin={isSuperAdmin}>
      <LegacyAppContent />
    </TenantProvider>
  );
}
